"use client"

import { useState, useEffect, useMemo } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../../shared/functions/Context"
import { dateFormat_YY_MM_DY_timeStamp } from "../../../shared/utils/utils"
import type { ILeadershipRating } from "../../../../shared/models/three-sixty-feedback-models/LeadershipRating"
import type { ITemplateRating } from "../../../../shared/models/three-sixty-feedback-models/TemplateRating"
import type { IValueRating } from "../../../../shared/models/three-sixty-feedback-models/ValueRating"
import type { ILeadership } from "../../../../shared/models/three-sixty-feedback-models/Leadership"
import type { IValue } from "../../../../shared/models/three-sixty-feedback-models/Values"
import QuestionnaireBoxResubmission from "../../questionnaire/components/QuestionnaireBoxResubmission"
import {
  RotateCcw,
  Download,
  Printer,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  FileText,
  Crown,
  Heart,
  Briefcase,
} from "lucide-react"

interface IProp {
  data: (ILeadershipRating | ITemplateRating)[]
  description: string
}

const ClientResubmissionTable = observer(({ data, description }: IProp) => {
  const { api, store } = useAppContext()
  const [selectedRating, setSelectedRating] = useState<IValue[] | ILeadership[] | any[]>([])
  const valueRatings = store.value.all.map((value) => value.asJson)
  const criteria = store.leadership.all.map((value) => value.asJson)
  const templates = store.templates.all.map((template) => template.asJson)

  const [selectedRow, setSelectedRow] = useState<ILeadershipRating | ITemplateRating | null>(null)
  const [showUserQuestionnaireBox, setShowUserQuestionnaireBox] = useState(false)
  const [showTable, setShowTable] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const ratingAssignments = store.ratingAssignments.all.find((r) => r.asJson.isActive === true)?.asJson

  const fetchData = async () => {
    await api.user.getAll()
    await api.templates.getAll()
    await api.templateRating.getAll()
    await api.ratingAssignments.getAll()
    await api.value.getAll()
    await api.leadership.getAll()
  }

  useEffect(() => {
    fetchData()
  }, [api.user, api.templates])

  const getTemplateTitle = (templateId: string | undefined): string => {
    if (!templateId) return ""
    const foundTemplate = templates.find((template) => template.id === templateId)
    return foundTemplate ? foundTemplate.title : templateId
  }

  const getDimensionIcon = (dimension: string) => {
    if (dimension === "Values") return <Heart className="h-4 w-4 text-blue-600" />
    if (dimension === "Leadership") return <Crown className="h-4 w-4 text-purple-600" />
    return <Briefcase className="h-4 w-4 text-green-600" />
  }

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const criteriaOrValues = "criteria" in item ? item.criteria : "values" in item ? item.values : {}
      const templateId = "templateId" in item ? item.templateId : undefined

      const matchesSearch =
        searchTerm === "" ||
        item.dimension.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getTemplateTitle(templateId).toLowerCase().includes(searchTerm.toLowerCase()) ||
        Object.values(criteriaOrValues || {}).some(
          (entry: any) =>
            entry.startDoing?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            entry.stopDoing?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            entry.continueDoing?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            entry.additionalComment?.toLowerCase().includes(searchTerm.toLowerCase()),
        )

      return matchesSearch
    })
  }, [data, searchTerm, templates])

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage)

  const handleRequestReRating = async (row: ILeadershipRating | ITemplateRating) => {
   

    if (row.dimension === "Values") {
      if (row.id) {
        const selectedRating = store.valueRating.getItemById(row.id)?.asJson
        setSelectedRating(valueRatings)
        if (selectedRating) {
          const updated: IValueRating = { ...selectedRating }
          console.log("Created rating:", updated)
        }
      }
      setSelectedRow(row)
      setShowUserQuestionnaireBox(true)
      setShowTable(false)
    } else if (row.dimension === "Leadership") {
      setSelectedRating(criteria)
      setSelectedRow(row)
      setShowUserQuestionnaireBox(true)
      setShowTable(false)
    } else {
      const templateRating = row as ITemplateRating
      const template = templates.find((t) => t.id === templateRating.templateId)

      if (template) {
        const templateCriteria = template.statements.map((stmt) => ({
          criteriaName: template.title,
          statements: [stmt],
        }))

        setSelectedRating(templateCriteria)
        setSelectedRow(row)
        setShowUserQuestionnaireBox(true)
        setShowTable(false)
      }
    }
  }

  const handleExportPDF = () => {
    console.log("Export PDF functionality")
  }

  const handlePrint = () => {
    console.log("Print functionality")
  }

  const handleBackToTable = () => {
    setShowUserQuestionnaireBox(false)
    setShowTable(true)
    setSelectedRow(null)
  }

  return (
    <div className="space-y-8">
      {showTable && (
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 p-6">
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center border-b border-slate-200 pb-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <RotateCcw className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">Resubmission Ratings Overview</h3>
                <p className="text-slate-600">Items requiring resubmission</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search ratings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={handlePrint}
                className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                title="Print"
              >
                <Printer className="h-5 w-5" />
              </button>
              <button
                onClick={handleExportPDF}
                className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                title="Export PDF"
              >
                <Download className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm text-slate-600 mb-4">
            <span>
              Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredData.length)} of{" "}
              {filteredData.length} ratings
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value))
                setCurrentPage(1)
              }}
              className="px-2 py-1 border border-slate-300 rounded text-sm"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Dimension
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Start Doing
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Stop Doing
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Continue Doing
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Comments
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {paginatedData.map((row, index) => {
                  const criteriaOrValues = "criteria" in row ? row.criteria : "values" in row ? row.values : {}
                  const firstKey = Object.keys(criteriaOrValues)[0]
                  const criteriaData = firstKey ? criteriaOrValues[firstKey] : null

                  return (
                    <tr
                      key={row.id}
                      className={`hover:bg-slate-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          <span className="text-sm text-slate-900">
                            {dateFormat_YY_MM_DY_timeStamp(
                              ("dateRequested" in row ? row.dateRequested : "date" in row ? row.date : Date.now()) ||
                                Date.now(),
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getDimensionIcon(row.dimension)}
                          <span className="text-sm font-medium text-slate-900">
                            {row.dimension === "Values" || row.dimension === "Leadership"
                              ? row.dimension
                              : getTemplateTitle("templateId" in row ? row.templateId : undefined)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="text-sm text-slate-900 max-w-xs truncate"
                          title={criteriaData?.startDoing || ""}
                        >
                          {criteriaData?.startDoing || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-900 max-w-xs truncate" title={criteriaData?.stopDoing || ""}>
                          {criteriaData?.stopDoing || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="text-sm text-slate-900 max-w-xs truncate"
                          title={criteriaData?.continueDoing || ""}
                        >
                          {criteriaData?.continueDoing || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="text-sm text-slate-900 max-w-xs truncate"
                          title={criteriaData?.additionalComment || ""}
                        >
                          {criteriaData?.additionalComment || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div
                            className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                              row.isflagged ? "bg-red-100 text-red-800" : "bg-orange-100 text-orange-800"
                            }`}
                          >
                            <RotateCcw className="h-3 w-3" />
                            Resubmission Required
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleRequestReRating(row)}
                          className="px-4 py-2 bg-orange-600 text-white hover:bg-orange-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                          title="Complete Resubmission"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Complete Rating
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {paginatedData.length === 0 && (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No ratings found</h3>
                <p className="text-slate-600">Try adjusting your search criteria.</p>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 mt-6">
              <div className="text-sm text-slate-600">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                          currentPage === pageNum
                            ? "bg-orange-600 text-white"
                            : "text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showUserQuestionnaireBox && selectedRow && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 p-4">
            <button
              onClick={handleBackToTable}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Table
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 p-6">
            <QuestionnaireBoxResubmission
              value={selectedRating}
              ratee={selectedRow.rateeId}
              rater={selectedRow.raterId}
              description={description}
              rating={selectedRow.id}
              templateId={"templateId" in selectedRow ? selectedRow.templateId : undefined}
            />
          </div>
        </div>
      )}
    </div>
  )
})

export default ClientResubmissionTable
